/* istanbul ignore file */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { ApolloError } from '@apollo/client';
import { useApplicationContext } from '@css-xp/base/common-context';
import { useFeatureToggle } from '@css-xp/base';
import { ContactType, useFetchEccFromContactService } from '@css-xp/contact-resources';
import {
  ConnectionSearchOptions,
  FilterOption,
  SortDirection,
  SortType,
  Maybe,
  ContactInfo,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DEFAULT_PAGE_SIZE, STATSIG_FEATURE_GATES, SUBSCRIPTION_CONSTANTS } from '@css-xp/ppc-constants';
import { useGetRelatedDomainsForCurrentTxA, useSortFromURLParams } from '@css-xp/ppc-custom-hooks';
import { useFetchProductsForTransactionAccountQuery } from '@css-xp/ppc-api';
import { SubscriptionTableFlat } from './subscription-table-flat';
import './subscription-table.scss';

interface SubscriptionTableContainerProps {
  transactionAccountId?: string;
  label?: string;
  shouldHideDownloadCsv?: boolean | null;
  filterOptions?: FilterOption[];
  testId?: string;
  isFreeTabEnabled?: boolean;
  loading?: boolean;
  error?: ApolloError | undefined;
  updateCsvFilterOptions?: (_value: FilterOption[]) => void;
  updateCsvSortOptions?: (_value?: Maybe<SortType>) => void;
  shouldHideCustomiseView?: boolean;
  shouldHideTabs?: boolean;
  isRowSelectionRequired?: boolean;
  isBulkTransferButtonRequired?: boolean;
  columnsToShow?: string[];
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  shouldAllowBillingProfileTransferBasedOnPricingEligibility?: boolean;
  allowedPricingEligibilityForBillingProfileTransfer?: string[];
  isSupportForExternallyBilledSubscriptionsEnabled?: boolean;
  isFutureDatedSubscriptionsEnabled?: boolean;
  isInternalUsageLicensesEnabled?: boolean;
  isTWCPhase1Enabled?: boolean;
  shouldFetchInternalLicenses?: boolean;
  showEnterpriseInstances?: boolean;
}

type RefetchParams =
  | Partial<{
      searchOptions: ConnectionSearchOptions;
      filterOptions: FilterOption[];
    }>
  | undefined;

export const SubscriptionTableFlatContainer = ({
  transactionAccountId,
  label,
  shouldHideDownloadCsv = false,
  filterOptions = [],
  testId,
  isFreeTabEnabled = true,
  loading = false,
  error,
  updateCsvFilterOptions,
  updateCsvSortOptions,
  shouldHideCustomiseView = false,
  isRowSelectionRequired = false,
  isBulkTransferButtonRequired = false,
  shouldHideTabs = false,
  columnsToShow,
  isSandboxSupportForSubscriptionsEnabled = false,
  shouldAllowBillingProfileTransferBasedOnPricingEligibility = false,
  allowedPricingEligibilityForBillingProfileTransfer = [],
  isSupportForExternallyBilledSubscriptionsEnabled = false,
  isFutureDatedSubscriptionsEnabled = false,
  isInternalUsageLicensesEnabled = false,
  isTWCPhase1Enabled = false,
  shouldFetchInternalLicenses = false,
  showEnterpriseInstances = false,
}: SubscriptionTableContainerProps) => {
  const [isEnabled] = useFeatureToggle();
  const isDefaultFiltersEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DEFAULT_FILTERS);
  // using ref as the filters is being passed in combinedRefetch method which should
  // take the latest value of the filters
  const filters = useRef<FilterOption[]>(filterOptions);
  const statusFilters = useRef<FilterOption[]>(
    shouldHideTabs
      ? isDefaultFiltersEnabled
        ? [
            {
              key: 'and',
              value: JSON.stringify([
                {
                  or: [
                    {
                      status: 'Active',
                    },
                    {
                      status: 'Upcoming',
                    },
                  ],
                },
              ]),
            },
          ]
        : []
      : [{ key: 'status', value: 'Active' }]
  );
  const { sortKey, sortOrder, filterOptionsFromUrl = [] } = useSortFromURLParams();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultSortOptions = {
    sortFields: [SUBSCRIPTION_CONSTANTS.SORT_KEYS.ID],
    sortFieldsDirections: [SortDirection.ASC],
  };
  if (sortKey && sortOrder) {
    defaultSortOptions.sortFields.unshift(sortKey);
    defaultSortOptions.sortFieldsDirections.unshift(sortOrder as SortDirection);
  } else {
    defaultSortOptions.sortFields.unshift(SUBSCRIPTION_CONSTANTS.SORT_KEYS.CLOUD_SITE);
    defaultSortOptions.sortFieldsDirections.unshift(SortDirection.ASC);
  }

  const [selectedSubscriptionIds, setSelectedSubscriptionIds] = React.useState<string[]>([]);
  const [allRowsSelected, setAllRowsSelected] = React.useState<boolean>(false);
  const [eccDetails, setEccDetails] = React.useState({});

  const { relatedDomains, isLoading: isRelatedDomainsLoading } = useGetRelatedDomainsForCurrentTxA();
  const { getBulkECCDetails } = useFetchEccFromContactService(ContactType.CUSTOMER_CONTACT);

  const onRowSelected = (selectedSubscriptionId: string) => {
    setSelectedSubscriptionIds((selectedSubscriptionIds) => {
      if (selectedSubscriptionIds.includes(selectedSubscriptionId)) {
        return selectedSubscriptionIds.filter((id) => id !== selectedSubscriptionId);
      }
      return [...selectedSubscriptionIds, selectedSubscriptionId];
    });
  };

  const onAllRowSelected = () => {
    const areAllRowsSelectedNow = allRowsSelected;
    if (areAllRowsSelectedNow) {
      setSelectedSubscriptionIds([]);
    }
    setAllRowsSelected((allRowsSelected) => !allRowsSelected);
  };

  const resetRowSelections = () => {
    setSelectedSubscriptionIds([]);
    setAllRowsSelected(false);
  };

  const {
    data,
    loading: isProductsForTxaLoading,
    error: essotError,
    refetch,
  } = useFetchProductsForTransactionAccountQuery({
    txaId: transactionAccountId ?? '',
    sortOptions: defaultSortOptions,
    statusFilter: statusFilters.current,
    additionalFilterOptions: [...filters.current, ...(filterOptionsFromUrl || [])],
    skipOption: !transactionAccountId || loading,
    isSandboxSupportForSubscriptionsEnabled,
    isSupportForExternallyBilledSubscriptionsEnabled,
    isTWCPhase1Enabled,
    shouldFetchInternalLicenses: shouldFetchInternalLicenses,
    relatedDomains: relatedDomains,
    showEnterpriseInstances,
  });

  useEffect(() => {
    filters.current = [...filterOptions];
  }, [filterOptions]);

  const totalCount = useMemo(() => {
    const totalCountData = shouldFetchInternalLicenses
      ? data?.transactionAccount?.internalUseSubscriptions
      : data?.transactionAccount?.productSubscriptionsInPPC;
    return totalCountData?.totalCount ?? 0;
  }, [data]);

  const subscriptions = useMemo(() => {
    const nodesData = shouldFetchInternalLicenses
      ? data?.transactionAccount?.internalUseSubscriptions?.nodes
      : data?.transactionAccount?.productSubscriptionsInPPC?.nodes;

    return nodesData || [];
  }, [data]);

  const getTransformDataToSubscriptionTableFormat = (subscriptions: any) => {
    // since we are fetching only products, so we are directly pushing it to product array
    // each node is a separate product in itself
    return subscriptions.map((node: any) => ({
      products: [node],
    }));
  };
  // although it says products and apps, but we are only fetching product as per current query
  // this is being used to send same data to SubscriptionTableFlat
  const productsAndApps = useMemo(() => subscriptions && getTransformDataToSubscriptionTableFormat(subscriptions), [
    subscriptions,
  ]);

  const combinedRefetch = useCallback(
    (refetchParams: RefetchParams, showApps = false) => {
      const filterOptions = [
        {
          key: 'not',
          value: showApps
            ? JSON.stringify([
                ...(isSandboxSupportForSubscriptionsEnabled ? [] : [SUBSCRIPTION_CONSTANTS.sandboxFilter]),
              ])
            : JSON.stringify([
                SUBSCRIPTION_CONSTANTS.addOnsFilter,
                SUBSCRIPTION_CONSTANTS.appsFilter,
                ...(isSandboxSupportForSubscriptionsEnabled ? [] : [SUBSCRIPTION_CONSTANTS.sandboxFilter]),
                ...(isTWCPhase1Enabled
                  ? [
                      SUBSCRIPTION_CONSTANTS.collectionFilter,
                      ...(showEnterpriseInstances ? [] : [SUBSCRIPTION_CONSTANTS.enterpriseFilter]),
                    ]
                  : []),
              ]),
        },
        ...filters.current,
        ...(filterOptionsFromUrl || []),
        ...(refetchParams?.filterOptions || []),
      ];
      updateCsvFilterOptions?.(filterOptions);
      updateCsvSortOptions?.(refetchParams?.searchOptions?.sort);
      refetch({
        searchOptions: {
          first: DEFAULT_PAGE_SIZE,
          offset: 0,
          sort: defaultSortOptions,
          ...refetchParams?.searchOptions,
        },
        filterOptions,
      });
    },
    [
      defaultSortOptions,
      filterOptionsFromUrl,
      isSandboxSupportForSubscriptionsEnabled,
      refetch,
      updateCsvFilterOptions,
      updateCsvSortOptions,
      showEnterpriseInstances,
    ]
  );

  const dataState = useMemo(() => {
    const isLoading = loading || isProductsForTxaLoading;
    const isDataLoading = shouldFetchInternalLicenses ? isLoading || isRelatedDomainsLoading : isLoading;

    return {
      data: productsAndApps,
      loading: isDataLoading,
      refetch: combinedRefetch,
      error: essotError?.toString() || error?.toString(),
      totalCount: totalCount,
    };
  }, [
    combinedRefetch,
    error,
    essotError,
    isProductsForTxaLoading,
    loading,
    productsAndApps,
    totalCount,
    isRelatedDomainsLoading,
  ]);

  const getContactDetails = useCallback(async () => {
    const billingGroupIdList: string[] = [];
    subscriptions?.forEach((subscription: any) => {
      if (subscription?.billingGroupId && !billingGroupIdList.includes(subscription.billingGroupId)) {
        billingGroupIdList.push(subscription?.billingGroupId);
      }
    });
    if (billingGroupIdList.length > 0) {
      try {
        const res = await getBulkECCDetails(billingGroupIdList, transactionAccountId);
        const eccData: { [key: string]: Array<ContactInfo> } = {};
        res.forEach((contact: ContactInfo) => {
          if (contact.identifierId) {
            if (eccData[contact.identifierId]) {
              eccData[contact.identifierId].push(contact);
            } else {
              eccData[contact.identifierId] = [contact];
            }
          }
        });
        setEccDetails(eccData);
      } catch (err) {
        setEccDetails([]);
      }
    }
  }, [subscriptions, transactionAccountId]);

  React.useEffect(() => {
    if (isInternalUsageLicensesEnabled && subscriptions?.length > 0) {
      getContactDetails();
    }
  }, [isInternalUsageLicensesEnabled, subscriptions, getContactDetails]);

  return (
    <SubscriptionTableFlat
      transactionAccountId={transactionAccountId}
      label={label}
      shouldHideDownloadCsv={shouldHideDownloadCsv}
      dataState={dataState}
      testId={testId}
      isPaginationEnabled={true}
      isFreeTabEnabled={isFreeTabEnabled}
      downloadCSVFilter={filterOptions}
      defaultSortOptions={defaultSortOptions}
      shouldHideCustomiseView={shouldHideCustomiseView}
      isRowSelectionRequired={isRowSelectionRequired}
      onRowSelected={onRowSelected}
      onAllRowSelected={onAllRowSelected}
      areAllRowsSelected={allRowsSelected}
      selectedSubscriptionIds={selectedSubscriptionIds}
      isBulkTransferButtonRequired={isBulkTransferButtonRequired}
      shouldHideTabs={shouldHideTabs}
      resetRowSelections={resetRowSelections}
      columnsToShow={columnsToShow}
      shouldAllowBillingProfileTransferBasedOnPricingEligibility={
        shouldAllowBillingProfileTransferBasedOnPricingEligibility
      }
      allowedPricingEligibilityForBillingProfileTransfer={allowedPricingEligibilityForBillingProfileTransfer}
      isFutureDatedSubscriptionsEnabled={isFutureDatedSubscriptionsEnabled}
      eccDetails={eccDetails}
      relatedDomains={relatedDomains}
      isInternalUsageLicensesEnabled={isInternalUsageLicensesEnabled}
      isTWCPhase1Enabled={isTWCPhase1Enabled}
      shouldFetchInternalLicenses={shouldFetchInternalLicenses}
    />
  );
};
